import { registerFunctionalComponent } from 'js/lib/components/component'
import { loadStripe } from '@stripe/stripe-js'
import { requestNative } from 'js/lib/api/api-request'
import { notifyBugsnag } from 'js/lib/bugsnag'
import { CHECKOUT_CLEAR_ERROR_EVENT, CHECKOUT_ERROR_EVENT } from './events'

registerFunctionalComponent('.js-c-payments-stripe-checkout', 'StripeCheckout', initStripeCheckout)

const PAYMENT_FORM_LOAD_ERROR_MESSAGE = 'Payment form failed to load. Please refresh the page to try again.'
const PAYMENT_LOADING_ERROR_MESSAGE = 'Payment form is still loading. Please wait a moment and try again.'
const ACTIONABLE_STRIPE_ERROR_TYPES = ['validation_error', 'card_error']
const PAYMENT_ELEMENT_OPTIONS = {
  fields: {
    billingDetails: {
      email: 'never',
      name: 'never',
      phone: 'never',
    },
  },
}

function initStripeCheckout(component) {
  const dom = buildDom(component.element)
  const config = {
    clientSecret: dom.root.dataset.clientSecret,
    publishableKey: window.PetRescue.env.stripe.publishableKey,
  }
  const state = {
    expressCheckoutElement: null,
    paymentElement: null,
    stripeCheckout: null,
    checkoutActions: null,
    unmounted: false,
    isSubmitting: false,
  }

  component.useHandler(() => setupStripeElements(dom, config, state))
  component.useHandler(() => setupFormSubmission(dom, state))
}

function buildDom(root) {
  const expressMountPoint = root.querySelector('.js-c-payments-stripe-checkout-express')
  const paymentMountPoint = root.querySelector('.js-c-payments-stripe-checkout-element')
  const submitButton = root.querySelector('.js-c-payments-stripe-checkout-submit')

  return {
    root,
    expressMountPoint,
    paymentMountPoint,
    submitButton,
    defaultSubmitText: submitButton?.textContent?.trim() || 'Pay now',
  }
}

function setupStripeElements(dom, config, state) {
  if (!hasRequiredMountPoints(dom)) {
    reportMissingMountPoints(dom)
    renderLoadError(dom, PAYMENT_FORM_LOAD_ERROR_MESSAGE)
    return () => cleanupStripeState(state)
  }

  initializeStripeCheckout(dom, config, state)
  return () => cleanupStripeElements(state)
}

async function initializeStripeCheckout(dom, config, state) {
  try {
    const stripe = await loadStripe(config.publishableKey)
    if (state.unmounted) return

    const checkout = await stripe.initCheckout({ clientSecret: config.clientSecret })

    if (state.unmounted) return

    state.stripeCheckout = checkout
    state.checkoutActions = null

    mountStripeElements(dom, state)
  } catch (error) {
    if (state.unmounted) return
    notifyBugsnag(error)
    renderLoadError(dom, PAYMENT_FORM_LOAD_ERROR_MESSAGE)
  }
}

function mountStripeElements(dom, state) {
  state.expressCheckoutElement = state.stripeCheckout.createExpressCheckoutElement()
  state.expressCheckoutElement.mount(dom.expressMountPoint)

  state.paymentElement = state.stripeCheckout.createPaymentElement(PAYMENT_ELEMENT_OPTIONS)
  state.paymentElement.mount(dom.paymentMountPoint)
}

function cleanupStripeElements(state) {
  cleanupStripeState(state)
  if (state.expressCheckoutElement) state.expressCheckoutElement.destroy()
  if (state.paymentElement) state.paymentElement.destroy()
}

function cleanupStripeState(state) {
  state.unmounted = true
  state.checkoutActions = null
}

function setupFormSubmission(dom, state) {
  const form = dom.root.closest('form')
  if (!form) return () => {}

  const onSubmit = event => handleSubmit(event, dom, state)
  form.addEventListener('submit', onSubmit)

  return () => form.removeEventListener('submit', onSubmit)
}

async function handleSubmit(event, dom, state) {
  event.preventDefault()

  const form = event.currentTarget
  if (!(form instanceof HTMLFormElement)) return
  if (state.isSubmitting) return

  if (!state.stripeCheckout) {
    dispatchCheckoutError(dom, { message: PAYMENT_LOADING_ERROR_MESSAGE })
    return
  }

  state.isSubmitting = true
  setLoading(dom, true)
  clearError(dom)

  try {
    await submitToPlatform(form)

    const error = await confirmCheckoutPayment(state)
    if (error) handleStripeError(error, dom)
  } catch (serverError) {
    handleServerError(serverError, dom)
  } finally {
    setLoading(dom, false)
    state.isSubmitting = false
  }
}

async function submitToPlatform(form) {
  const formData = new FormData(form)

  await requestNative({
    url: form.getAttribute('action') || window.location.href,
    type: 'POST',
    data: formData,
    accept: 'json',
  })
}

async function confirmCheckoutPayment(state) {
  const actions = await loadCheckoutActions(state)
  const { error } = await actions.confirm()
  return error
}

async function loadCheckoutActions(state) {
  if (state.checkoutActions) return state.checkoutActions

  const result = await state.stripeCheckout.loadActions()

  if (result.type === 'error') {
    throw result.error || new Error('Failed to initialize checkout actions')
  }

  state.checkoutActions = result.actions
  return state.checkoutActions
}

function handleStripeError(error, dom) {
  if (isUserActionableStripeError(error)) {
    dispatchCheckoutError(dom, { message: error.message })
    return
  }

  dispatchCheckoutError(dom)
  console.error(error)
  notifyBugsnag(error)
}

function handleServerError(serverError, dom) {
  const handled = dispatchCheckoutError(dom, buildServerErrorPayload(serverError))

  if (handled || hasDisplayableServerErrors(serverError)) return

  console.error(serverError)
  notifyBugsnag(serverError)
}

function setLoading(dom, loading) {
  if (!dom.submitButton) return

  dom.submitButton.disabled = loading
  dom.submitButton.textContent = loading ? 'Processing...' : dom.defaultSubmitText
}

function renderLoadError(dom, message) {
  const target = dom.paymentMountPoint || dom.root

  if (target === dom.root) {
    dispatchCheckoutError(dom, { message })
    return
  }

  target.innerHTML = ''

  const paragraph = document.createElement('p')
  paragraph.className = 'tw-text-red-600 tw-text-sm'
  paragraph.textContent = message

  target.appendChild(paragraph)
}

function clearError(dom) {
  dispatchClearConfiguredErrors(dom)
}

function dispatchCheckoutError(dom, error) {
  dispatchClearConfiguredErrors(dom)

  const event = new CustomEvent(CHECKOUT_ERROR_EVENT, {
    bubbles: true,
    cancelable: true,
    detail: error,
  })

  return !dom.root.dispatchEvent(event)
}

function dispatchClearConfiguredErrors(dom) {
  dom.root.dispatchEvent(new CustomEvent(CHECKOUT_CLEAR_ERROR_EVENT, { bubbles: true }))
}

function buildServerErrorPayload(serverError) {
  const errors = Array.isArray(serverError?.errors) ? serverError.errors.filter(Boolean) : []
  const message = typeof serverError?.message === 'string' && serverError.message.trim() ? serverError.message : null

  return {
    error_type: serverError?.error_type,
    errors,
    message,
  }
}

function hasDisplayableServerErrors(serverError) {
  return Boolean(serverError?.error_type)
    || Array.isArray(serverError?.errors) && serverError.errors.length > 0
    || Boolean(typeof serverError?.message === 'string' && serverError.message.trim())
}

function isUserActionableStripeError(error) {
  return ACTIONABLE_STRIPE_ERROR_TYPES.includes(error?.type) && Boolean(error?.message)
}

function hasRequiredMountPoints(dom) {
  return Boolean(dom.expressMountPoint && dom.paymentMountPoint)
}

function reportMissingMountPoints(dom) {
  const missingMountPoints = [!dom.expressMountPoint && 'express', !dom.paymentMountPoint && 'payment']
    .filter(Boolean)
    .join(', ')

  notifyBugsnag(new Error(`Stripe checkout mount point missing: ${missingMountPoints}`))
}
